import { API_BASE_URL } from "../config/api";
import { getAuthToken, getCookie } from "../utils/cookieUtils";

/**
 * Get the WebSocket URL derived from API_BASE_URL
 */
export function getWebSocketUrl() {
  const httpUrl = API_BASE_URL;
  const wsProtocol = httpUrl.startsWith("https") ? "wss:" : "ws:";
  const host = httpUrl.replace(/^https?:\/\//, "");
  return `${wsProtocol}//${host}/ws/presence`;
}

class PresenceSocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Set();
    this.reconnectTimer = null;
    this.reconnectAttempts = 0;
    this.maxReconnectDelay = 30000;
    this.isConnected = false;
    this.lastPresence = {
      activeOnline: 0,
      onBreak: 0,
      totalRegistered: 0,
      activeEmployeeIds: [],
      onBreakEmployeeIds: [],
    };
  }

  /**
   * Connect to the presence WebSocket server
   */
  connect(forcedToken = null) {
    if (typeof window === "undefined") return;
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const token = forcedToken || getAuthToken() || getCookie("token");
    if (!token) {
      return;
    }

    try {
      const wsUrl = `${getWebSocketUrl()}?token=${encodeURIComponent(token)}`;
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
      };

      this.socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.event === "presence_update" && message.data) {
            this.lastPresence = {
              ...this.lastPresence,
              ...message.data,
            };
            this.notifyListeners(this.lastPresence);
          }
        } catch (err) {
          console.warn("[PresenceSocket] Error parsing message:", err);
        }
      };

      this.socket.onclose = () => {
        this.isConnected = false;
        this.scheduleReconnect();
      };

      this.socket.onerror = (err) => {
        console.warn("[PresenceSocket] Connection error:", err.message || err);
        this.socket?.close();
      };
    } catch (err) {
      console.warn("[PresenceSocket] Failed to create WebSocket:", err);
      this.scheduleReconnect();
    }
  }

  /**
   * Schedule exponential backoff reconnect
   */
  scheduleReconnect() {
    if (this.reconnectTimer) return;
    const delay = Math.min(1000 * Math.pow(1.5, this.reconnectAttempts), this.maxReconnectDelay);
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  /**
   * Send break state to server (for employees)
   */
  setBreakStatus(isOnBreak) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(
        JSON.stringify({
          action: "set_break_status",
          isOnBreak: Boolean(isOnBreak),
        })
      );
    }
  }

  /**
   * Subscribe to live presence updates
   */
  subscribe(listener) {
    if (typeof listener !== "function") return () => {};
    this.listeners.add(listener);

    // Deliver last known presence immediately
    if (this.lastPresence) {
      listener(this.lastPresence);
    }

    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all registered listeners
   */
  notifyListeners(data) {
    for (const listener of this.listeners) {
      try {
        listener(data);
      } catch (err) {
        console.error("[PresenceSocket] Listener error:", err);
      }
    }
  }

  /**
   * Disconnect socket and clean up
   */
  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.close();
      this.socket = null;
    }
    this.isConnected = false;
  }
}

const presenceSocket = new PresenceSocketService();
export default presenceSocket;
